import { useL10n } from "@ews/react-localization-context";
import { eventDetails, eventGetSymbol, formatLogicalAddress, NumberGroup, timestampToDate } from "@ews/zlt-events";
import { IonButton, IonCol, IonLabel, IonRow } from "@ionic/react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import FilterList, { isFilterActive } from "../../../FilterList/FilterList";
import List from "../../../List/List";
import { useMyPermissions, useMyProfile } from "../../../Login/LoginProvider";
import { MaintenanceLogItem, useReportSystemMaintenanceLog, useSystemDetails, useSystemMaintenance } from "../../../ReactData/system";
import { useIsMobile, useIsNative } from "../../../Responsive";
import DatePicker, { dateRange, dateStringToValue, valueToDateString } from "../../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker";
import SettingsSelect from "../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import style from "./MaintenanceReport.module.scss";
import { MaintenanceReportFilterKeys, MaintenanceReportProps } from "./types";

import Icon, { IconName } from "@ews/react-icons";
import jsonToCsvExport from "json-to-csv-export";
import { FilterState } from "../../../FilterList/types";
import { usePersistentState } from "../../../Persistence";
import { T_GROUP_LIST } from "../../SystemSettings/types";
import { Filter } from "../types/reportTypes";

import { DateTimeFormatMaintenanceReport, formatDateMaintenanceReport } from "@ews/react-data";
import { document as documentIcon } from "ionicons/icons";
import { countryTranslationsKeyTemplate } from "../../../../setup/countries/countries";
import { Locale } from "../../../../setup/locale";
import { deepEqual } from "../../../../utils";
import { HeaderOption, ListProps } from "../../../List/types";
import { RouteParams, useNamedRoutes } from "../../../NamedRoutes";
import MaintenanceSidebar from "../../../Navigation/Sidebar/Maintenance";
import { MenuLink } from "../../../Navigation/Sidebar/Sidebar";
import { hasPermissionMeasurements } from "../../../Navigation/Sidebar/System";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import { PrintHeader, PrintHeaderRow } from "./components/MaintenanceSetting/PrintHeader/PrintHeader";
import { handlePrint, usePrintHotkeyListener } from "../../../Print";




const defaultRange = dateRange(new Date(), 1);

type PersistenceFilter = MaintenanceReportFilterKeys & Filter<FilterState & { showComments?: boolean; }, keyof MaintenanceLogItem | `${keyof MaintenanceLogItem},${keyof MaintenanceLogItem}`>;


const GroupSort: Record<string, number> = {
    "Zone": 1,
    "ActuationDevice": 2,
    "AlarmingDevice": 3,
    "TransmissionDevice": 4,
};


const standardFilter: PersistenceFilter = {
    timestamp: valueToDateString(defaultRange),
    status: "",
    sortCriteria: "zone,element",
    sortOrder: "ASC",
    //pageSize: pageRange[1]
};


export const getLogEventTranslationText = (value: string | undefined) =>
{
    if (!value) return "TD_GROSS_ALLE";
    else {
        return `[REACT_REPORT_MAINTENANCE_LOG_ELEMENTS_${value.toUpperCase()}]`;
    }
};

const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];
const link = { url: "system:elementinfo", paramsKey: "numberGroup,zone,element" };

const MaintenanceReport: React.FC<MaintenanceReportProps> = ({
    system,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{

    const { generate, params } = useNamedRoutes();

    const mobileView = useIsMobile();
    const isNative = useIsNative();

    const { translate: t } = useL10n();
    //const myDetails = useMyDetails();
    const me = useMyProfile();
    const filterData = useReportSystemMaintenanceLog();
    const systemDetails = useSystemDetails(system.id || "");

    const [list, setList] = useState<(MaintenanceLogItem)[]>([]);

    const [currentState, setState] = usePersistentState<PersistenceFilter>('systems-maintenance:filter', { ...standardFilter } as PersistenceFilter);
    const myPermission = useMyPermissions();



    const maintenanceSettings = useSystemMaintenance(system.id || "");


    usePrintHotkeyListener(handlePrint);

    const isFilterChanged = isFilterActive(currentState, standardFilter, ignoreKeys); // richtige version


    const onReloadChange = useCallback(() => setState((prev) => ({ ...prev })), [setState]);


    const onMenu: ListProps["onMenu"] = useCallback((id: any, onMenuToggle: () => void, onSimulate: (() => void) | null, routeParams: RouteParams): ReactNode =>
    {
        const { numberGroup, zone, element } = routeParams;

        return <MaintenanceSidebar onMenuToggle={onMenuToggle}>
            <MenuLink
                title={t('[REACT_SHOW_COMMENTS]')}
                link={generate('system:element-comment', { numberGroup: `${numberGroup}`, group: `${zone}`, element: `${element}` })}
                icon={documentIcon}
            />
        </MaintenanceSidebar>;
    }, [generate, t]);





    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState]);




    const getElementLogicalAddressText = useCallback((zone: number, element: number, numberGroup: number) =>
    {
        const T_GROUP = T_GROUP_LIST[(numberGroup || NumberGroup.Zone) as keyof typeof T_GROUP_LIST] || "";

        return `${t(T_GROUP)} ${formatLogicalAddress(zone, element, numberGroup, maintenanceSettings.formatLeadingZeros)}`;

    }, [maintenanceSettings.formatLeadingZeros, t]);

    const getDateTimeFormat = useCallback((timestamp: number | undefined) =>
    {
        return timestamp ? formatDateMaintenanceReport(
            me.locale,
            maintenanceSettings.dateTimeFormat,
            timestampToDate(timestamp)
        ) : "";
    }, [maintenanceSettings.dateTimeFormat, me.locale]);

    const getFormatDateRange = (value: PersistenceFilter["timestamp"]) =>
    {
        if (!value) return "";

        return value.split(",").map(date =>
        {
            console.log(date);
            const timestamp = new Date(date);
            return formatDateMaintenanceReport(
                me.locale,
                DateTimeFormatMaintenanceReport.date,//maintenanceSettings.dateTimeFormat,
                timestamp
            );
        }).join(" - ");


    };

    const headerOptions: HeaderOption<Locale, MaintenanceLogItem>[] = useMemo(() =>
    {

        const basicHeader: HeaderOption<Locale, MaintenanceLogItem>[] = [
            {
                title: 'TD_ELEM2',
                //className: `${style["col"]} ${mobileView ? style["sizeFull"] : ` ${style["nowrap"]}`}`,
                className: `${style["col"]} ${mobileView ? style["sizeFull"] : `${style["size2"]} ${style["nowrap"]}`}`,
                key: 'zone,element',
                both: true,
                child: ({ element, zone, numberGroup }) =>
                {
                    return <>{getElementLogicalAddressText(zone, element, numberGroup)}</>;
                }
            },
            {
                title: '[REACT_REPORT_MAINTENANCE_LOG_EVENT]',
                className: `${style["col"]} ${mobileView ? style["sizeFull"] : style["size4"]}`,
                key: 'eventKindType',
                both: true,
                child: ({ eventKindType, zone, element, softwareDeviceType }) =>
                {
                    return <>
                        <div className={style["iconContainer"]}>
                            {eventKindType ? <><Icon height={30} width={30} icon={eventGetSymbol(eventKindType, { zone, element }) as IconName} /> {t(eventDetails(eventKindType, softwareDeviceType)?.translationKey || "unknown")}</> : ""}
                        </div>
                    </>;
                }
            },
            {
                title: 'TD_TEXT_1',
                key: 'groupText',
                both: true,
                className: `${style["col"]}`
            },
            {
                title: 'TD_TEXT_2',
                key: 'elementText',
                both: true,
                className: `${style["col"]}`
            },
            {
                title: '[REACT_REPORT_MAINTENANCE_LOG_DATE]',
                className: `${style["col"]} ${mobileView ? style["sizeFull"] : style["size3"]}`,
                key: 'timestamp',
                both: true,
                child: ({ timestamp }) =>
                {
                    return <>
                        {getDateTimeFormat(timestamp)}
                    </>;
                }
            },

        ];



        if (currentState.showComments) {
            return [...basicHeader,
            {
                title: "[REACT_COMMENT]",
                both: true,
                key: "commentText",
                className: `${style["col"]}`
            },
            {
                title: "COM_BANNERS_FIELD_IMAGE_LABEL",
                both: true,
                key: "commentImage",
                className: `${style.center} ${style["col"]}`,
                child: ({ commentImage }) => commentImage ? <img className="maintenanceCommentImage" src={commentImage} width={50} height={50} alt="" /> : <></>
            }];
        }
        return basicHeader;
    }
        , [getDateTimeFormat, getElementLogicalAddressText, mobileView, t, currentState.showComments]);


    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);


    const getFilteredData = useCallback(async (id: string, query: PersistenceFilter) =>
    {
        const response = await filterData(id, query);
        if (!response) return;

        const list = response.sort((a, b) =>
        {
            const valA = GroupSort[NumberGroup[a.numberGroup]] || 0;
            const valB = GroupSort[NumberGroup[b.numberGroup]] || 0;
            return valA - valB;
        });

        setList(pre =>
        {
            const equal = deepEqual(pre, list);
            if (equal) return pre;

            return list;
        });

    }, [filterData]);

    useEffect(() =>
    {
        if (system.id) getFilteredData(system.id, currentState);
    }, [currentState, system, maintenanceSettings, getFilteredData]);



    if (!hasPermissionMeasurements(myPermission)) {
        return <></>;
    }

    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <FilterList
            isOpen={filter?.isFilterOpen || false}
            isDetailsOpen={filter?.isDetailOpen || false}
            onIsOpen={onIsFilterOpen}
            onReset={onResetFilter}
            noPrint
        >
            <IonCol size="3">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("[REACT_REPORT_MAINTENANCE_LOG_EVENT]")}</IonLabel>
                </div>
                <SettingsSelect text={""} value={currentState.status} onSelect={(value) =>
                {
                    const currentFilter = { ...currentState };
                    currentFilter.status = value;
                    setState({ ...currentFilter });
                }}>
                    <SettingsSelectOption text={"TD_GROSS_ALLE"} value={''}></SettingsSelectOption>
                    <SettingsSelectOption text={"[REACT_REPORT_MAINTENANCE_LOG_ELEMENTS_TESTED]"} value={'tested'}></SettingsSelectOption>
                    <SettingsSelectOption text={"[REACT_REPORT_MAINTENANCE_LOG_ELEMENTS_UNTESTED]"} value={'untested'}></SettingsSelectOption>

                </SettingsSelect>
                <SettingsCheckbox text="[REACT_SHOW_COMMENTS]" checked={currentState.showComments || false} onChange={(showComments) => setState({ ...currentState, showComments })} />
            </IonCol>
            <IonCol size="3">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("[REACT_REPORT_MAINTENANCE_LOG_DATE]")}</IonLabel>
                </div>
                <DatePicker

                    childText={{ start: "[REACT_START]", end: "[REACT_END]" }}
                    value={dateStringToValue(currentState.timestamp!)}
                    divider={false}
                    position="row"
                    onChange={(range) =>
                    {
                        const currentFilter = { ...currentState };
                        currentFilter.timestamp = valueToDateString(range);
                        setState({ ...currentFilter });
                    }}

                />
            </IonCol>
            { /* Disable download buttons when in mApp mode (Bug #13046) */
                isNative ? null :
                    <IonCol style={{ display: 'flex', flexDirection: "column", alignItems: "end" }}>
                        <IonRow className={`${style.center} ${style.marginTopBottom}`} >
                            <IonButton

                                color={"success"}
                                onClick={async () =>
                                {
                                    jsonToCsvExport({
                                        data: list.map(item =>
                                        {
                                            return {
                                                'logicalAddress': getElementLogicalAddressText(item.zone, item.element, item.numberGroup),//formatLogicalAddress(item.zone, item.element, item.numberGroup, maintenanceSettings.formatLeadingZeros),
                                                'eventKindType': item.eventKindType ? t(eventDetails(item.eventKindType, item.softwareDeviceType || 0)?.translationKey || "unknown") : "",
                                                "commentText": item.commentText,
                                                'elementText': item.elementText,
                                                'groupText': item.groupText,
                                                'timestamp': getDateTimeFormat(item.timestamp)
                                            };
                                        }),
                                        delimiter: ";",
                                        filename: `${t('COM_FACPMANAGEMENT_USER_ACCOUNT_MAINTENANCE')}_${system.systemNumber}_${Date.now()}`,
                                        headers: [
                                            { key: 'logicalAddress', label: t('TD_ELEM2') },
                                            { key: 'eventKindType', label: t('[REACT_REPORT_MAINTENANCE_LOG_EVENT]') },
                                            { key: 'commentText', label: t('[REACT_COMMENT]') },
                                            { key: 'elementText', label: t('TD_TEXT_1') },
                                            { key: 'groupText', label: t('TD_TEXT_2') },
                                            { key: 'timestamp', label: t('[REACT_REPORT_MAINTENANCE_LOG_DATE]') }
                                        ]
                                    });
                                }}
                            >
                                <span> {`${t('[REACT_DOWNLOAD]', { filetype: "CSV" })}`}</span>
                            </IonButton>

                        </IonRow>

                        <IonRow className={`${style.center} ${style.marginTopBottom}`}>
                            <IonButton onClick={handlePrint} color={"success"}>
                                {t('[REACT_DOWNLOAD]', { filetype: "PDF" })}
                            </IonButton>
                        </IonRow>
                    </IonCol>
            }
        </FilterList >


        <PrintHeader
            title={
                <>
                    <p>newReact - {t("[REACT_MAINTENANCE_REPORTS]")}</p>
                    <img alt="logo" src='assets/img/App-Icon-ReACT_invert.png' />
                </>
            }
        >
            <PrintHeaderRow data={[
                system.customerName || "",
                `${t("[REACT_ALARM_COUNTER_LABEL]")}: ${0}`]} />

            <PrintHeaderRow data={[
                system.name || "",
                ``]} />

            <PrintHeaderRow data={[
                system.location || "",
                ``]} />

            <PrintHeaderRow data={[
                systemDetails?.street || "",
                ``]} />

            <PrintHeaderRow data={[
                `${systemDetails.zip || ""} ${systemDetails.city || ""}`,
                `${t("[REACT_REPORT_MAINTENANCE_LOG_EVENT]")}: ${t(getLogEventTranslationText(currentState.status))}`]} />

            <PrintHeaderRow data={[
                systemDetails.country ? t(countryTranslationsKeyTemplate(systemDetails.country)) : "",
                `${t("[REACT_ALARM_TIME_RANGE]")}: ${getFormatDateRange(currentState.timestamp)}`]} />

        </PrintHeader>

        <List
            rowContainerClass={style.row}
            rows={list}
            noPagination
            onMenu={onMenu}
            link={link}
            onReload={onReloadChange}
            headers={headerOptions}
        />
    </div>;
};

export default MaintenanceReport;
